
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { loadWidget, truncateContent } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { Getter, namespace } from 'vuex-class';
import moment from 'moment';
import {
  IAPIKeys,
  IDashboardCount,
  ILogsCount,
  IScheduledEvent,
  ISenderID,
  IUser,
} from '@/types/types';
import { Watch } from 'vue-property-decorator';
const authModule = namespace('auth');
const apiKeyModule = namespace('apiKeys');
const reportsModule = namespace('reports');
const smsModule = namespace('sms');
const usersModule = namespace('users');
const eventsModule = namespace('events');

const KX_FONT = 'Hanken Grotesk, system-ui, sans-serif';
const KX_MUTED = '#4a545f';
const KX_GRID = '#edf0f4';
/**
 * Delivery status series palette. Order and hexes are CVD-validated
 * (dataviz six checks) against the white card surface:
 * DELIVERED green, ACCEPTED blue, UNDELIVERED orange, EXPIRED violet,
 * REJECTED red.
 */
const DELIVERY_COLORS = ['#008300', '#2a78d6', '#eb6834', '#4a3aa7', '#e34948'];

const numberFormatter = new Intl.NumberFormat('en-GH');
const currencyFormatter = new Intl.NumberFormat('en-GH', {
  style: 'currency',
  currency: 'GHS',
  minimumFractionDigits: 2,
});

@Component({
  name: 'Dashboard',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    DoubleDatePicker: loadWidget('widgets/DoubleDatePicker'),
    AddSMSSenderID: loadWidget(`widgets/AddSenderID`),
  },
  filters: {
    timestamp(value: string, format?: string) {
      return moment(value).format(format ?? 'Do MMMM, YYYY');
    },
    truncateContent,
  },
})
export default class Dashboard extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @apiKeyModule.Getter('getAPIKeys') accessKeys!: IAPIKeys;
  @reportsModule.Getter('getDashboardCount') count!: IDashboardCount;
  @reportsModule.Getter('getDailyDeliveryStats') dailyReports!: Array<{
    key: string;
    logs: ILogsCount;
  }>;
  @smsModule.Getter('getSenderIDList') smsIDList!: Array<
    ISenderID & { uuid: string; id: number; slug: string }
  >;

  @authModule.Getter('getUserDetails') user!: IUser;
  @eventsModule.Getter('getAllEvents') events!: Array<
    IScheduledEvent & { start: string }
  >;
  @usersModule.Getter('getSearchResults') subAccounts!: IUser[];

  query = '';

  picker = '';
  minDate = '';

  rangeMenu = false;
  showCustomPicker = false;
  selectedRange = '30d';
  customDates: string[] = [];
  rangePresets: Array<{ key: string; label: string }> = [
    { key: 'today', label: 'Today' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'mtd', label: 'Month to date' },
  ];

  get rangeLabel(): string {
    if (this.selectedRange === 'custom' && this.customDates.length === 2) {
      const [start, end] = [...this.customDates].sort();
      return `${moment(start).format('D MMM')} – ${moment(end).format(
        'D MMM'
      )}`;
    }
    const preset = this.rangePresets.find(
      (item) => item.key === this.selectedRange
    );
    return preset?.label ?? 'Last 30 days';
  }

  get heroTiles(): Array<{
    label: string;
    icon: string;
    tooltip: string;
    value: string;
  }> {
    const logs = this.count?.logs;
    const totalSent = logs?.totalSmsCount ?? 0;
    const delivered = logs?.totalSmsDelivered ?? 0;
    return [
      {
        label: 'Total Sent SMS',
        icon: 'send',
        tooltip: 'Overall SMS sent from this account across all statuses',
        value: numberFormatter.format(totalSent),
      },
      {
        label: 'Delivered SMS',
        icon: 'task_alt',
        tooltip: `Indicates that notification has been delivered to customers' handset`,
        value: numberFormatter.format(delivered),
      },
      {
        label: 'Delivery Rate',
        icon: 'percent',
        tooltip: 'Share of sent SMS that reached the handset',
        value:
          totalSent > 0
            ? `${((delivered / totalSent) * 100).toFixed(1)}%`
            : '0%',
      },
      {
        label: 'Undelivered SMS',
        icon: 'sms_failed',
        tooltip: `Indicates that notification was unable to reach customers' handset due to various reasons eg. Network`,
        value: numberFormatter.format(logs?.totalSmsUndelivered ?? 0),
      },
    ];
  }

  get miniStats(): Array<{ label: string; tooltip: string; value: string }> {
    const logs = this.count?.logs;
    const stats = [
      {
        label: 'Accepted',
        tooltip: `Received by the provider, completion notification still pending`,
        value: numberFormatter.format(logs?.totalSmsAccepted ?? 0),
      },
      {
        label: 'Expired',
        tooltip: `Couldn't reach the handset before the provider expired it`,
        value: numberFormatter.format(logs?.totalSmsExpired ?? 0),
      },
      {
        label: 'Rejected',
        tooltip: 'Rejected by the provider before delivery was attempted',
        value: numberFormatter.format(logs?.totalSmsRejected ?? 0),
      },
      {
        label: 'Groups',
        tooltip: 'Total groups created',
        value: numberFormatter.format(this.count?.groupCount ?? 0),
      },
      {
        label: 'Contacts',
        tooltip: 'Total contacts available on this account',
        value: numberFormatter.format(this.count?.contactCount ?? 0),
      },
      {
        label: 'Sender IDs',
        tooltip: 'Sender IDs registered on this account',
        value: numberFormatter.format(this.smsIDList?.length ?? 0),
      },
      {
        label: 'Upcoming Events',
        tooltip: 'Scheduled events that are yet to run',
        value: numberFormatter.format(this.upcomingEvents?.length ?? 0),
      },
    ];
    if (this.user?.businessId && this.user?.businessId?.isBusinessReseller) {
      stats.push({
        label: 'SubAccounts',
        tooltip: 'Total sub accounts',
        value: numberFormatter.format(this.count?.subAccountsCount ?? 0),
      });
    }
    if (this.user && !this.user.isSubAccount) {
      stats.push({
        label: 'Payments',
        tooltip: 'Overall total credit purchases',
        value: currencyFormatter.format(this.count?.totalCreditPurchases ?? 0),
      });
    }
    return stats;
  }

  /**
   * Chart options are intentionally static: series carry their own x values
   * ({ x, y } points), so options identity never changes and vue-apexcharts
   * never runs its crash-prone updateOptions diff.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartOptions: any = {
    chart: {
      type: 'line',
      fontFamily: KX_FONT,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    noData: { text: 'No data for this period', style: { fontFamily: KX_FONT } },
    colors: DELIVERY_COLORS,
    legend: {
      fontFamily: KX_FONT,
      position: 'top',
      horizontalAlign: 'left',
      markers: { radius: 12 },
      itemMargin: { horizontal: 10 },
    },
    grid: {
      show: true,
      borderColor: KX_GRID,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
      hover: { size: 5 },
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
    xaxis: {
      type: 'category',
      labels: { style: { colors: KX_MUTED, fontFamily: KX_FONT } },
      axisBorder: { color: KX_GRID },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { style: { colors: KX_MUTED, fontFamily: KX_FONT } },
      title: {
        text: 'Messages count',
        style: {
          fontSize: '11px',
          fontFamily: KX_FONT,
          color: KX_MUTED,
          fontWeight: 500,
        },
      },
    },
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartBarOptions: any = {
    chart: {
      type: 'bar',
      fontFamily: KX_FONT,
      toolbar: { show: false },
    },
    legend: { show: false },
    grid: {
      show: true,
      borderColor: KX_GRID,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end',
        columnWidth: '45%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      labels: { style: { colors: KX_MUTED, fontFamily: KX_FONT } },
      axisBorder: { color: KX_GRID },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: KX_MUTED, fontFamily: KX_FONT },
        // counts are integers; hide fractional ticks apex generates for small domains
        formatter: (value: number): string =>
          Number.isInteger(value) ? `${value}` : '',
      },
    },
    noData: { text: 'No data yet', style: { fontFamily: KX_FONT } },
  };

  // computed so the options objects keep a stable identity across re-renders
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get topBarOptions(): any {
    return { ...this.chartBarOptions, colors: ['#14294f'] };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get bottomBarOptions(): any {
    return { ...this.chartBarOptions, colors: ['#ff0055'] };
  }

  series: Array<{ name: string; data: Array<{ x: string; y: number }> }> = [];

  // top sender address graph
  topSenderAddressGraphSeries: Array<{
    name: string;
    data: Array<{ x: string; y: number }>;
  }> = [];
  bottomSenderAddressGraphSeries: Array<{
    name: string;
    data: Array<{ x: string; y: number }>;
  }> = [];

  eventsCalendar: string[] = [];
  upcomingEvents: Array<IScheduledEvent & { start: string }> = [];

  @Watch('events')
  onScheduledEventsChange(
    value: Array<IScheduledEvent & { start: string }>
  ): void {
    this.eventsCalendar = value.map((event) =>
      moment(event.start).format('YYYY-MM-DD')
    );
    this.upcomingEvents = value
      .filter((event) => event.state === 'SCHEDULED')
      .slice(0, 6);
  }

  @Watch('dailyReports')
  onDailyReportsChange(dailyReports: {
    deliveryReceiptGraph: Array<{ key: string; logs: ILogsCount }>;
    topSenderAddressesGraph: Array<{
      _id: string;
      totalSMSViaSenderAddress: number;
    }>;
    bottomSenderAddressesGraph: Array<{
      _id: string;
      totalSMSViaSenderAddress: number;
    }>;
  }): void {
    // series carry their own x values so chart options stay static
    const graph = dailyReports?.deliveryReceiptGraph ?? [];
    const points = (pick: (logs: ILogsCount) => number) =>
      graph.map((report) => ({ x: report.key, y: pick(report.logs) }));
    // series order follows DELIVERY_COLORS (validated palette order)
    this.series = [
      { name: 'DELIVERED', data: points((logs) => logs.delivered) },
      { name: 'ACCEPTED', data: points((logs) => logs.accepted) },
      { name: 'UNDELIVERED', data: points((logs) => logs.undelivered) },
      { name: 'EXPIRED', data: points((logs) => logs.expired) },
      { name: 'REJECTED', data: points((logs) => logs.rejected) },
    ];
    this.topSenderAddressGraphSeries = [
      {
        name: 'Messages',
        data: (dailyReports?.topSenderAddressesGraph ?? []).map((sender) => ({
          x: sender._id,
          y: sender.totalSMSViaSenderAddress,
        })),
      },
    ];
    this.bottomSenderAddressGraphSeries = [
      {
        name: 'Messages',
        data: (dailyReports?.bottomSenderAddressesGraph ?? []).map(
          (sender) => ({
            x: sender._id,
            y: sender.totalSMSViaSenderAddress,
          })
        ),
      },
    ];
  }

  selectRange(key: string): void {
    this.selectedRange = key;
    this.showCustomPicker = false;
    this.customDates = [];
    this.rangeMenu = false;
    const today = moment().format('YYYY-MM-DD');
    const ranges: { [key: string]: [string, string] } = {
      today: [today, today],
      '7d': [moment().subtract(6, 'day').format('YYYY-MM-DD'), today],
      '30d': [moment().subtract(29, 'day').format('YYYY-MM-DD'), today],
      '90d': [moment().subtract(89, 'day').format('YYYY-MM-DD'), today],
      mtd: [moment().startOf('month').format('YYYY-MM-DD'), today],
    };
    const [startDate, endDate] = ranges[key];
    this.query = `?startDate=${startDate}&endDate=${endDate}`;
    this.$store.dispatch('reports/dailyDeliveryStats', this.query);
  }

  applyCustomRange(): void {
    const [startDate, endDate] = [...this.customDates].sort();
    this.selectedRange = 'custom';
    this.showCustomPicker = false;
    this.rangeMenu = false;
    this.query = `?startDate=${startDate}&endDate=${endDate}`;
    this.$store.dispatch('reports/dailyDeliveryStats', this.query);
  }

  closeCustomPicker(): void {
    this.showCustomPicker = false;
    this.customDates = [];
  }

  refreshPage(): void {
    this.$store.dispatch('reports/dailyDeliveryStats', this.query);
  }
  created(): void {
    this.minDate = moment().subtract(3, 'month').format('YYYY-MM-DD');
    this.$store.dispatch('reports/dashboardCount');
    this.$store.dispatch('reports/dailyDeliveryStats');
    this.picker = moment().format('YYYY-MM-DD');
    this.$store.dispatch('sms/listSenderID', null);
    this.$store.dispatch('events/list', {
      query: `?activeDate=${moment().format('YYYY-MM-DD')}&isPaginated=false`,
    });
    if (!this.subAccounts || this.subAccounts.length === 0) {
      this.$store.dispatch('users/rawList', {
        query: `?rawList=true`,
      });
    }
  }
}
